import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from '../supabaseClient';
import { applyTopicRecall } from '../store/workspacesSlice';
import { captureConnectionsEvent } from '../lib/captureConnectionsEvent';
import { getBrowserTimeZone } from '../lib/browserTimeZone';

const RIPE_TARGET = 3;

const selectTopicRecallInfo = (state, topicId) => {
  const fromBasket = state.workspaces.currentBasket?.topics?.find(
    (t) => t.id === topicId
  );
  if (fromBasket) {
    return {
      recallCount: fromBasket.recall_count ?? 0,
      basketId: fromBasket.basket_id ?? state.workspaces.currentBasket?.id ?? null,
    };
  }

  const workspace = state.workspaces.currentWorkspace;
  const fromFlat = workspace?.topics?.find((t) => t.id === topicId);
  if (fromFlat) {
    return {
      recallCount: fromFlat.recall_count ?? 0,
      basketId: fromFlat.basket_id ?? null,
    };
  }

  for (const basket of workspace?.baskets || []) {
    const topic = basket.topics?.find((t) => t.id === topicId);
    if (topic) {
      return {
        recallCount: topic.recall_count ?? 0,
        basketId: topic.basket_id ?? basket.id ?? null,
      };
    }
  }

  return { recallCount: 0, basketId: null };
};

/**
 * Manages recall-ripeness state for a single topic.
 *
 * Reads the live recall count from the Redux store (the source of truth) and
 * records recalls through the `record_recall` Supabase RPC, dispatching an
 * optimistic store update on success. Only `loading` and `error` are local.
 *
 * @param {string} topicId - The topic the recall applies to.
 * @returns {{
 *   recallCount: number,
 *   isRipe: boolean,
 *   loading: boolean,
 *   error: Error | null,
 *   // Resolves true only when the RPC succeeded and the store was updated.
 *   record: (recalled: boolean) => Promise<boolean>,
 * }}
 */
export const useRecall = (topicId) => {
  const dispatch = useDispatch();

  const { recallCount, basketId } = useSelector((state) =>
    selectTopicRecallInfo(state, topicId)
  );
  const isRipe = recallCount >= RIPE_TARGET;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const record = useCallback(
    async (recalled) => {
      setLoading(true);
      setError(null);

      try {
        const { error: rpcError } = await supabase.rpc('record_recall', {
          p_topic_id: topicId,
          p_recalled: recalled,
        });

        if (rpcError) {
          setError(rpcError);
          return false;
        }

        // Mirror applyTopicRecall outcome for analytics
        const recallCountAfter =
          recalled === true ? (recallCount ?? 0) + 1 : (recallCount ?? 0) - 1;
        const isRipeAfter = recallCountAfter >= RIPE_TARGET;

        captureConnectionsEvent('topic_recall_submitted', {
          topic_id: topicId,
          basket_id: basketId || undefined,
          was_recalled: recalled === true,
          recall_count_after: recallCountAfter,
          is_ripe_after: isRipeAfter,
        });

        void supabase.auth
          .getSession()
          .then(({ data: { session } }) => {
            if (!session?.access_token) return;

            return fetch('/api/brevo-contact', {
              method: 'POST',
              keepalive: true,
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ action: 'sync', timezone: getBrowserTimeZone() }),
            }).then((response) => {
              if (!response.ok) {
                console.warn('Brevo recall sync failed:', response.status);
              }
            });
          })
          .catch((syncError) => {
            console.warn('Brevo recall sync error:', syncError);
          });

        dispatch(applyTopicRecall({ topicId, recalled }));
        return true;
      } catch (err) {
        setError(err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, topicId, recallCount, basketId]
  );

  return { recallCount, isRipe, loading, error, record };
};
